import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftRight, ArrowRight, ExternalLink, Pencil } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge, type BadgeProps } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/Select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { EditStockMovementDialog } from '@/components/inventory/EditStockMovementDialog';
import {
  ADJUSTMENT_REASON_LABELS,
  STOCK_MOVEMENT_TYPE_LABELS,
  useStockMovementSources,
  useStockMovements,
  type AdjustmentReason,
  type StockMovementRow,
  type StockMovementType,
} from '@/features/inventory/hooks';
import { useAuth } from '@/app/AuthProvider';
import { canWrite } from '@/lib/permissions';
import { formatDateTime } from '@/lib/formatters';
import { formatQtyUom } from '@/lib/uom';
import { cn } from '@/lib/utils';

const MOVEMENT_LIMIT = 500;
const PAGE_SIZE = 25;

const TYPE_VARIANT: Record<StockMovementType, NonNullable<BadgeProps['variant']>> = {
  receipt: 'success',
  issue: 'warning',
  transfer: 'info',
  adjustment: 'secondary',
  cycle_count: 'secondary',
  production_consume: 'warning-soft',
  production_produce: 'info-soft',
};

const FILTERS = {
  all: { label: 'All movements', types: null },
  production: { label: 'Production', types: ['production_produce', 'production_consume'] },
  receipts: { label: 'Receipts & returns', types: ['receipt'] },
  adjustments: { label: 'Adjustments & counts', types: ['adjustment', 'cycle_count'] },
  transfers: { label: 'Transfers & issues', types: ['transfer', 'issue'] },
} as const satisfies Record<string, { label: string; types: readonly StockMovementType[] | null }>;

type FilterId = keyof typeof FILTERS;

function inFilter(filter: FilterId, type: StockMovementType): boolean {
  const types: readonly StockMovementType[] | null = FILTERS[filter].types;
  return !types || types.includes(type);
}

const SOURCE_FALLBACK: Record<string, string> = {
  production_form: 'Production form',
  goods_receipt: 'Goods receipt',
  return: 'Customer return',
  work_order: 'Work order',
};

/**
 * Signed change to the item's on-hand. Transit locations are left out,
 * matching the On-hand total, so a move between two stocking locations
 * nets to zero.
 */
function onHandDelta(m: StockMovementRow): number {
  const qty = Number(m.quantity);
  const into = m.to_location_id && m.to?.kind !== 'transit' ? qty : 0;
  const out = m.from_location_id && m.from?.kind !== 'transit' ? qty : 0;
  return into - out;
}

function reasonLabel(code: string | null): string | null {
  if (!code) return null;
  if (code === 'reversal') return 'Reversal';
  return ADJUSTMENT_REASON_LABELS[code as AdjustmentReason] ?? code;
}

/**
 * Every stock movement that touched this item, newest first, with the
 * running on-hand after each one so the On-hand total can be traced back
 * to the production forms, receipts, and adjustments that built it.
 * Source documents open in their own pages; the row opens the movement
 * correction dialog.
 */
export function ItemMovementsCard({
  itemId,
  baseUom,
  onHand,
}: {
  itemId: string;
  baseUom: string;
  /** Current on-hand (non-transit), used to walk the running balance backwards. */
  onHand: number | null;
}) {
  const { role } = useAuth();
  const canManage = canWrite(role, 'inventory');
  const { data, isLoading } = useStockMovements({ itemId, limit: MOVEMENT_LIMIT });
  const { data: sources } = useStockMovementSources(data);
  const [filter, setFilter] = useState<FilterId>('all');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [editing, setEditing] = useState<StockMovementRow | null>(null);

  const rows = useMemo(() => {
    let balance = onHand;
    return (data ?? []).map((m) => {
      const delta = onHandDelta(m);
      const after = balance;
      if (balance != null) balance -= delta;
      return { movement: m, delta, after };
    });
  }, [data, onHand]);

  const filtered = useMemo(
    () => rows.filter((r) => inFilter(filter, r.movement.type)),
    [rows, filter],
  );

  const productionCount = useMemo(
    () => rows.filter((r) => inFilter('production', r.movement.type)).length,
    [rows],
  );

  const shown = filtered.slice(0, visible);
  const truncated = (data?.length ?? 0) >= MOVEMENT_LIMIT;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1.5">
            <CardTitle>Movements</CardTitle>
            <p className="text-xs text-muted-foreground">
              Everything that added to or took from on-hand
              {productionCount > 0 &&
                ` · ${productionCount.toLocaleString()} from production`}
              {canManage && ' · click a row to correct it'}
            </p>
          </div>
          <Select
            value={filter}
            onValueChange={(v) => {
              setFilter(v as FilterId);
              setVisible(PAGE_SIZE);
            }}
          >
            <SelectTrigger className="h-9 w-[190px] text-sm" aria-label="Filter movements">
              {FILTERS[filter].label}
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(FILTERS) as FilterId[]).map((id) => (
                <SelectItem key={id} value={id}>
                  {FILTERS[id].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={ArrowLeftRight}
            title={filter === 'all' ? 'No movements yet' : `No ${FILTERS[filter].label.toLowerCase()} movements`}
          />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead className="text-right">Change</TableHead>
                  <TableHead
                    className="text-right"
                    title="On-hand right after this movement, worked back from today's total"
                  >
                    Balance
                  </TableHead>
                  <TableHead>Lot</TableHead>
                  <TableHead>Status / memo</TableHead>
                  {canManage && <TableHead className="w-10" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {shown.map(({ movement: m, delta, after }) => {
                  const source =
                    m.reference_type && m.reference_id
                      ? sources?.get(`${m.reference_type}:${m.reference_id}`)
                      : undefined;
                  const reason = reasonLabel(m.reason_code);
                  return (
                    <TableRow
                      key={m.id}
                      className={cn(
                        canManage && 'cursor-pointer',
                        m.status === 'reversed' && 'opacity-60',
                      )}
                      onClick={canManage ? () => setEditing(m) : undefined}
                    >
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {formatDateTime(m.created_at)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={TYPE_VARIANT[m.type] ?? 'secondary'}>
                          {STOCK_MOVEMENT_TYPE_LABELS[m.type] ?? m.type}
                        </Badge>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {source ? (
                          <Link
                            to={source.href}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 font-mono text-xs text-foreground hover:underline"
                          >
                            {source.label}
                            <ExternalLink className="h-3 w-3 text-muted-foreground" />
                          </Link>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            {(m.reference_type && SOURCE_FALLBACK[m.reference_type]) ?? 'Manual'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="flex items-center gap-1.5 whitespace-nowrap text-sm">
                          <span className={m.from ? 'text-foreground' : 'text-muted-foreground'}>
                            {m.from?.name ?? '—'}
                          </span>
                          <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground" />
                          <span className={m.to ? 'text-foreground' : 'text-muted-foreground'}>
                            {m.to?.name ?? '—'}
                          </span>
                        </span>
                      </TableCell>
                      <TableCell
                        className={cn(
                          'whitespace-nowrap text-right tabular-nums',
                          delta > 0 && 'text-bright-moss-dark',
                          delta < 0 && 'text-fresh-salmon-dark',
                        )}
                      >
                        {delta === 0 ? (
                          <span className="text-muted-foreground">
                            {formatQtyUom(Number(m.quantity), baseUom)} moved
                          </span>
                        ) : (
                          `${delta > 0 ? '+' : '−'}${formatQtyUom(Math.abs(delta), baseUom)}`
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-right tabular-nums text-muted-foreground">
                        {after == null ? '—' : after.toLocaleString()}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {m.lots?.lot_number ?? '—'}
                      </TableCell>
                      <TableCell className="max-w-[280px]">
                        <span className="flex flex-wrap items-center gap-1.5">
                          {m.reverses_movement_id ? (
                            <Badge variant="outline">Reversal</Badge>
                          ) : m.status === 'reversed' ? (
                            <Badge variant="muted">Reversed</Badge>
                          ) : null}
                          {reason && reason !== 'Reversal' && <Badge variant="outline">{reason}</Badge>}
                          {m.memo ? (
                            <span className="truncate text-xs text-muted-foreground" title={m.memo}>
                              {m.memo}
                            </span>
                          ) : (
                            !m.reverses_movement_id &&
                            m.status !== 'reversed' &&
                            !reason && <span className="text-muted-foreground">—</span>
                          )}
                        </span>
                      </TableCell>
                      {canManage && (
                        <TableCell className="text-right">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditing(m);
                            }}
                            aria-label="Edit movement"
                            title="Edit movement"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
              <span>
                Showing {shown.length.toLocaleString()} of {filtered.length.toLocaleString()}
                {truncated && ` (latest ${MOVEMENT_LIMIT.toLocaleString()} movements)`}
              </span>
              <div className="flex items-center gap-2">
                {shown.length < filtered.length && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setVisible((v) => v + PAGE_SIZE * 4)}
                  >
                    Show more
                  </Button>
                )}
                <Button asChild type="button" variant="ghost" size="sm">
                  <Link to="/inventory/movements">Full movement log</Link>
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
      {canManage && (
        <EditStockMovementDialog
          open={editing !== null}
          onOpenChange={(o) => !o && setEditing(null)}
          movement={editing}
        />
      )}
    </Card>
  );
}
